"use client"

import { Link, useLocation } from "react-router-dom"
import { cn } from "@/lib/utils"
import { useAdminAuth } from "@/lib/admin-auth-context"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  LayoutDashboard,
  Package,
  FolderTree,
  ShoppingCart,
  ImageIcon,
  Languages,
  Settings,
  LogOut,
  Leaf,
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
  Home,
  Bot,
  Menu,
  X,
} from "lucide-react"
import { useState, useEffect } from "react"
import { useTheme, type Theme } from "@/lib/theme-context"

const menuItems = [
  { href: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { href: "/admin/products", label: "Mahsulotlar", icon: Package },
  { href: "/admin/categories", label: "Kategoriyalar", icon: FolderTree },
  { href: "/admin/orders", label: "Buyurtmalar", icon: ShoppingCart },
  { href: "/admin/hero-slides", label: "Hero Slaydlar", icon: ImageIcon },
  { href: "/admin/translations", label: "Tillar", icon: Languages },
  { href: "/admin/telegram", label: "Telegram Bot", icon: Bot },
  { href: "/admin/settings", label: "Sozlamalar", icon: Settings },
]

export function AdminSidebar() {
  const { pathname } = useLocation()
  const { user, logout } = useAdminAuth()
  const { theme, setTheme } = useTheme()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    setMobileOpen(false)
  }, [pathname])

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }
    return () => {
      document.body.style.overflow = "unset"
    }
  }, [mobileOpen])

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden bg-white dark:bg-gray-900 shadow-lg rounded-xl h-10 w-10"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </Button>

      {mobileOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />}

      <aside
        className={cn(
          "fixed left-0 top-0 z-40 h-screen bg-white dark:bg-gray-900 border-r border-slate-200 dark:border-gray-800 transition-all duration-300 shadow-xl",
          // Desktop
          "hidden lg:block",
          collapsed ? "lg:w-[80px]" : "lg:w-[280px]",
        )}
      >
        <SidebarContent
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          pathname={pathname}
          user={user}
          logout={logout}
          theme={theme}
          setTheme={setTheme}
        />
      </aside>

      <aside
        className={cn(
          "fixed left-0 top-0 z-50 h-screen w-[280px] bg-white dark:bg-gray-900 border-r border-slate-200 dark:border-gray-800 transition-transform duration-300 shadow-xl lg:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <SidebarContent
          collapsed={false}
          setCollapsed={() => { }}
          pathname={pathname}
          user={user}
          logout={logout}
          theme={theme}
          setTheme={setTheme}
          isMobile
          onClose={() => setMobileOpen(false)}
        />
      </aside>
    </>
  )
}

function SidebarContent({
  collapsed,
  setCollapsed,
  pathname,
  user,
  logout,
  theme,
  setTheme,
  isMobile = false,
  onClose,
}: {
  collapsed: boolean
  setCollapsed: (value: boolean) => void
  pathname: string
  user: any
  logout: () => void
  theme: Theme
  setTheme: (theme: Theme) => void
  isMobile?: boolean
  onClose?: () => void
}) {
  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="h-20 flex items-center justify-between px-4 border-b border-slate-200 dark:border-gray-800 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20">
        <Link to="/admin" className="flex items-center gap-3" onClick={onClose}>
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30 flex-shrink-0">
            <Leaf className="h-6 w-6 text-white" />
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <span className="font-bold text-lg text-gray-900 dark:text-white block">Admin</span>
              <p className="text-xs text-muted-foreground truncate">Boshqaruv paneli</p>
            </div>
          )}
        </Link>
        {isMobile ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
          >
            <X className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="h-8 w-8 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-6">
        <nav className="px-3 space-y-2">
          {menuItems.map((item) => {
            const isActive = pathname === item.href || (item.href !== "/admin" && pathname.startsWith(item.href))
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={onClose}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                  isActive
                    ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/30"
                    : "text-gray-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white",
                )}
              >
                <item.icon className={cn("h-5 w-5 flex-shrink-0", isActive && "text-white")} />
                {!collapsed && <span className="font-medium truncate">{item.label}</span>}
              </Link>
            )
          })}
        </nav>
      </ScrollArea>

      {/* Bottom section */}
      <div className="p-4 border-t border-slate-200 dark:border-gray-800 space-y-3 bg-slate-50 dark:bg-gray-800/50">
        {/* View Site */}
        <Link to="/" target="_blank" onClick={onClose}>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start gap-3 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/20",
              collapsed && !isMobile && "justify-center px-0",
            )}
          >
            <Home className="h-5 w-5 text-emerald-600 flex-shrink-0" />
            {(!collapsed || isMobile) && <span className="truncate">Saytni ko'rish</span>}
          </Button>
        </Link>





        {/* Logout */}
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-start gap-3 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20",
            collapsed && !isMobile && "justify-center px-0",
          )}
          onClick={() => {
            logout()
            onClose?.()
          }}
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {(!collapsed || isMobile) && <span className="truncate">Chiqish</span>}
        </Button>
      </div>
    </div>
  )
}
